import {
  Home02,
  Logout03,
  User03,
  FaceID,
  Shield02,
  CreditCard01,
  Wallet02,
  FileCheck02,
  MessageCircle01
} from "untitledui-js-base";
import PetsIcon from "../../../shared/icons/pets.svg?react";

import { compactNumber, cx } from "shared/utils";
import { TabStepsType } from "views/applications-id-report";
import {
  Verification,
  VerificationNames,
  VerificationStatus,
  PartnerFeatures
} from "shared/types";
import { StatusIcon } from "shared/app/VerificationStatusIcon";
import {
  mapCombinedVerificationNameToTitle,
  mapCombinedVerificationToStatus
} from "utils/combineVerifications";
import { Attachment02 } from "untitledui-js-base";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Select, Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { Application } from "types/application";
import { useChatContext } from "stream-chat-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useVerificationsOverviewController,
  VERIFICATIONS_WITH_WARNINGS
} from "shared/queries/useVerificationsOverviewController";
import { Loader } from "shared/components/Loader";
import { HugeiconsIcon } from "@hugeicons/react";
import { SquareLock01Icon } from "@hugeicons-pro/core-stroke-rounded";

const mapTabNameToIcon = (name: TabStepsType | string) => {
  switch (name) {
    case VerificationNames.RentalApplication:
      return <User03 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.IdentityVerification:
      return <FaceID className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.CombinedIncome:
    case VerificationNames.IncomeVerification:
    case VerificationNames.VerifiedIncomeVerification:
      return <Wallet02 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.HousingHistory:
      return <Home02 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.CreditReport:
      return <CreditCard01 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.EvictionReport:
      return <Logout03 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.CriminalReport:
    case VerificationNames.CanadianCriminalReport:
      return <Shield02 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.SubmissionDisclosure:
    case VerificationNames.TermsAndConditions:
      return <FileCheck02 className="shrink-0 stroke-inherit" size="20px" />;
    case VerificationNames.PetScreeningReport:
      return <PetsIcon className="shrink-0 stroke-inherit" />;
    default:
      return;
  }
};

interface SidebarProps {
  applicantId: number;
  application: Application;
  tabSteps: (Verification | Verification[])[];
  currentTab: TabStepsType;
  setCurrentTab: (tabName: TabStepsType) => void;
  isInvitee?: boolean;
}

const PAGE_PADDING = 24,
  HEADER_HEIGHT = 180;

export const Sidebar = ({
  applicantId,
  application,
  tabSteps: verifications,
  currentTab,
  setCurrentTab,
  isInvitee
}: SidebarProps) => {
  const { data } = useQuery({
    queryFn: () =>
      instance
        .get(`/portal/v1/applicants/${applicantId}/additional_documents`, {
          timeout: 300_000 // 5 minutes
        })
        .then(({ data }) => data),
    queryKey: ["ADDITIONAL_DOCUMENTS", applicantId]
  });

  const { verificationsOverviewMeta } = useVerificationsOverviewController({
    applicationId: application.id,
    applicantId: applicantId
  });

  const { type, partnerFeatures, permissions } = useAuthContext();

  const hasPermission = useCallback(
    (name: TabStepsType) => {
      switch (name) {
        case VerificationNames.RentalApplication:
          return permissions.canAccessReports.rentalApplication;
        case VerificationNames.IdentityVerification:
          return permissions.canAccessReports.identityVerification;
        case VerificationNames.IncomeVerification:
        case VerificationNames.CombinedIncome:
        case VerificationNames.VerifiedIncomeVerification:
          return permissions.canAccessReports.incomeReport;
        case VerificationNames.HousingHistory:
          return permissions.canAccessReports.housingHistoryReport;
        case VerificationNames.CreditReport:
          return permissions.canAccessReports.creditReport;
        case VerificationNames.CriminalReport:
          return permissions.canAccessReports.usCriminalReport;
        case VerificationNames.EvictionReport:
          return permissions.canAccessReports.housingCourtReport;
        case VerificationNames.CanadianCriminalReport:
          return permissions.canAccessReports.caCriminalReport;
        case VerificationNames.PetScreeningReport:
          return permissions.canAccessReports.animalScreening;
        case VerificationNames.SubmissionDisclosure:
          return permissions.canAccessReports.disclosure;
        case "additional_documents":
          return permissions.canAccessReports.additionalDocumentsReport;
        default:
          return true;
      }
    },
    [permissions]
  );

  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat) && !isInvitee;
  const currentApplicant = application.applicants.find(
    (app) => Number(app.id) === Number(applicantId)
  );

  const isIdentityVerificationOverrideRequired =
    application.requires_overrides_identity_verification?.applicants.some(
      ({ id }) => id === applicantId
    );

  const { client } = useChatContext();

  const channel = isInvitee
    ? null
    : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- that's expected
      client?.getChannelById("messaging", `chat-id-${applicantId}`, {
        watch: true
      });

  const [unreads, setUnreads] = useState(0);

  useEffect(() => {
    const channelWatcher = async () => {
      if (!channel) return;

      await channel.watch();

      setUnreads(channel.countUnread());

      channel.on(() => {
        setUnreads(channel.countUnread());
      });
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- expected
    if (channel) channelWatcher();
  }, [channel, isInvitee]);

  const navigationOptions = useMemo(() => {
    const ret = hasChat
      ? [
          {
            value: "chat",
            label: "Chat"
          }
        ]
      : [];

    ret.push(
      ...verifications
        .filter((tabStep: Verification | Verification[]) => {
          if (Array.isArray(tabStep)) return tabStep.length === 2;

          return true;
        })
        .map((tabStep: Verification | Verification[]) => {
          const verification = Array.isArray(tabStep) ? tabStep[0] : tabStep;
          return {
            value: String(verification.name),
            label: String(mapCombinedVerificationNameToTitle(tabStep))
          };
        })
    );

    ret.push({
      value: "additional_documents",
      label: "Additional documents"
    });

    return ret;
  }, [hasChat]);

  const hasPendingCountyReports = Boolean(
    currentApplicant?.pending_county_reports
  );

  return (
    <>
      <div
        className={cx(
          "no-print -top-5 mb-8 p-2 lg:sticky lg:mb-0 lg:p-4 md:block hidden",
          // h-px is used to keep sticky sidebar sticky
          "lg:h-[750px]"
        )}
      >
        {/* Chat with applicant */}
        <Show
          if={
            type.isBoomScreen &&
            hasChat &&
            Boolean(!currentApplicant?.is_manual)
          }
        >
          <div
            className={cx(
              "flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg p-3 transition-all duration-150",
              currentTab === "chat"
                ? "rounded-lg bg-primary-20 stroke-primary-500"
                : "stroke-content-1000 hover:bg-alpha-40"
            )}
            onClick={() => {
              document.getElementById("report_section")?.scrollTo({
                top: HEADER_HEIGHT + PAGE_PADDING,
                behavior: "smooth"
              });
              setCurrentTab("chat");
            }}
          >
            <div className="flex flex-shrink w-full items-center gap-2 stroke-inherit relative">
              <MessageCircle01
                className="shrink-0 stroke-inherit"
                size="20px"
              />
              <p
                className={cx(
                  "truncate text-sm font-medium",
                  currentTab === "chat"
                    ? "text-primary-500"
                    : "text-content-1000"
                )}
              >
                Chat
              </p>
              {unreads ? (
                <div className="bg-danger-500 text-white rounded text-xxs px-1 ml-auto min-w-[16px] text-center">
                  {compactNumber(unreads)}
                </div>
              ) : null}
            </div>
          </div>

          <div className="h-px bg-alpha-80 mt-4" />
        </Show>
        {verifications
          .filter((tabStep: Verification | Verification[]) => {
            if (Array.isArray(tabStep)) return tabStep.length === 2;

            return true;
          })
          .map((tabStep: Verification | Verification[]) => {
            const verification = Array.isArray(tabStep) ? tabStep[0] : tabStep;

            const isSelected = currentTab === verification.name;

            const notStartedOrInProgressOrSkipped = [
              VerificationStatus.Pending,
              VerificationStatus.Started,
              VerificationStatus.Skipped
            ].includes(verification.status);

            const notStartedClassnames = notStartedOrInProgressOrSkipped
              ? "text-content-100"
              : "text-content-1000";

            return (
              <div
                className={cx(
                  "my-1 flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg px-3 py-2 transition-all duration-150",
                  isSelected && "rounded-lg bg-primary-20 stroke-primary-500",
                  notStartedOrInProgressOrSkipped
                    ? "stroke-content-100"
                    : "stroke-content-1000 hover:bg-alpha-40"
                )}
                onClick={() => {
                  document.getElementById("report_section")?.scrollTo({
                    top: HEADER_HEIGHT + PAGE_PADDING,
                    behavior: "smooth"
                  });
                  setCurrentTab(verification.name as TabStepsType);
                }}
                key={verification.name}
              >
                <div className="flex flex-shrink items-center gap-2 stroke-inherit">
                  {mapTabNameToIcon(verification.name)}
                  <p
                    className={cx(
                      "truncate text-sm font-medium",
                      isSelected ? "text-primary-600" : notStartedClassnames
                    )}
                  >
                    {mapCombinedVerificationNameToTitle(tabStep)}
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  {VERIFICATIONS_WITH_WARNINGS.includes(verification.name) &&
                  verificationsOverviewMeta.isLoading ? (
                    <Loader className="size-4 animate-appear" />
                  ) : (
                    <>
                      <Show if={hasPermission(verification.name)}>
                        <StatusIcon
                          hasWarnings={
                            verificationsOverviewMeta.data?.[verification.name]
                              ?.has_warnings || false
                          }
                          isSsnMismatched={
                            verificationsOverviewMeta.data?.[
                              VerificationNames.CreditReport
                            ]?.ssn_mismatched || false
                          }
                          isAwaitingForCounty={
                            verification.name ===
                              VerificationNames.CriminalReport &&
                            hasPendingCountyReports
                          }
                          applicantStatus={
                            application.applicants.find(
                              (app) => app.id === applicantId
                            )?.application_status
                          }
                          isIdentityVerificationOverrideRequired={Boolean(
                            isIdentityVerificationOverrideRequired
                          )}
                          verificationStatus={mapCombinedVerificationToStatus(
                            verification
                          )}
                          isManuallyOverridden={
                            verification.status ===
                            VerificationStatus.FinishedByOverridden
                          }
                          name={verification.name}
                          executionMode={verification.settings?.execution_mode}
                          applicationId={application.id}
                          applicantId={applicantId}
                          verificationId={verification.id}
                          isRerun={verification.is_rerun}
                          isSelected={isSelected}
                        />
                      </Show>
                      <Show if={!hasPermission(verification.name)}>
                        <HugeiconsIcon
                          icon={SquareLock01Icon}
                          size={16}
                          className={cx(
                            "shrink-0 stroke-inherit",
                            isSelected && "text-primary-500",
                            notStartedClassnames
                          )}
                          strokeWidth={2}
                        />
                      </Show>
                    </>
                  )}
                </div>
              </div>
            );
          })}

        <div className="border-t border-alpha-40 my-4" />

        <div
          className={cx(
            "my-1 flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg p-3 transition-all duration-150",
            currentTab === "additional_documents"
              ? "rounded-lg bg-primary-20 stroke-primary-500"
              : "stroke-content-1000 hover:bg-alpha-40"
          )}
          onClick={() => {
            document.getElementById("report_section")?.scrollTo({
              top: HEADER_HEIGHT + PAGE_PADDING,
              behavior: "smooth"
            });
            setCurrentTab("additional_documents");
          }}
        >
          <div className="flex flex-shrink items-center gap-2 stroke-inherit">
            <Attachment02 className="shrink-0 stroke-inherit" size="20px" />
            <p
              className={cx(
                "truncate text-sm font-medium",
                currentTab === "additional_documents"
                  ? "text-primary-500"
                  : "text-content-1000"
              )}
            >
              Additional documents
            </p>
          </div>
          <div className="flex items-center justify-center">
            <p
              className={cx(
                "text-sm font-medium",
                currentTab === "additional_documents"
                  ? "text-primary-600"
                  : "text-content-1000"
              )}
            >
              {hasPermission("additional_documents") ? (
                data?.total_count || 0
              ) : (
                <HugeiconsIcon
                  icon={SquareLock01Icon}
                  size={16}
                  className={cx(
                    "shrink-0 stroke-inherit",
                    currentTab === "additional_documents" && "text-primary-500"
                  )}
                  strokeWidth={2}
                />
              )}
            </p>
          </div>
        </div>
      </div>

      <Select
        id="report-selector"
        className="md:hidden"
        onChange={(e) => {
          setCurrentTab(e.target.value as TabStepsType);
        }}
        value={currentTab}
        options={navigationOptions}
      />
    </>
  );
};
